import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { BeatLoader } from 'react-spinners';
import { useAppSelector } from '../../hooks';
import { postAnalyticsEvent } from '../../lib/analytics';
import { createDownloadRequest, fetchDownloadRequest } from '../../lib/api';
import { DownloadRequest, ResourceData } from '../../lib/types';
import { appSelectors } from '../../redux/appSlice';
import UsageConsentForm from '../UsageConsentForm';

interface DownloadStatusProps {
  downloadRequest?: DownloadRequest | null;
}

const DownloadStatus = ({ downloadRequest }: DownloadStatusProps) => {
  if (downloadRequest?.status === 'completed' && downloadRequest.url) {
    return (
      <>
        <p>
          Thank you for using the age-positive image library. Please click the
          button below to download your images.
        </p>
        <p>
          <a href={downloadRequest.url} target="_blank">
            Download your images
          </a>
        </p>
      </>
    );
  }

  if (downloadRequest?.status === 'failed' && downloadRequest.error) {
    return (
      <>
        <p>There was an error while processing your request.</p>
        <p className="error">{downloadRequest.error}</p>
        <p>Please try again later.</p>
      </>
    );
  }

  return (
    <>
      <div className="loading">
        <BeatLoader color="#000000" />
      </div>
      <p>
        Thank you for downloading images from the age-positive image library.
      </p>
      <p>
        <strong>
          Please keep this browser window open while we prepare your files.
        </strong>{' '}
        This might take a while, depending on the total size of your resources.
      </p>
      <p>To continue working, please open a new browser window.</p>
    </>
  );
};

interface DownloadAllProps {
  resourceData?: ResourceData[];
  collectionId?: number;
  collectionSize?: number;
}

export const DownloadAll = ({
  resourceData,
  collectionId,
  collectionSize,
}: DownloadAllProps) => {
  const isLoggedIn = useAppSelector(appSelectors.isLoggedIn);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [size, setSize] = useState<string>('');
  const [downloadId, setDownloadId] = useState<number | undefined>(undefined);
  const [statusCheck, setStatusCheck] = useState<boolean>(false);

  const resourceIds = useMemo<number[] | undefined>(
    () => resourceData?.map((resource) => resource.ref),
    [resourceData]
  );

  const { data: downloadRequest } = useQuery({
    queryKey: ['downloadRequest', downloadId],
    queryFn: () => (downloadId ? fetchDownloadRequest(downloadId) : null),
    enabled: statusCheck && !!downloadId,
    refetchInterval: 3000,
  });

  useEffect(() => {
    const usage_consent = Cookies.get('image_library_usage_consent');
    if (!isLoggedIn && !showForm && usage_consent !== '1') {
      setShowForm(true);
    }
  }, [isLoggedIn, showForm]);

  useEffect(() => {
    if (downloadRequest?.status === 'completed' && !statusCheck) {
      setStatusCheck(false);
    }
  }, [downloadRequest?.status, statusCheck]);

  const onSubmit = () => {
    setShowForm(false);
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSize(e.target.value);
  };

  const handleDownloadClick = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      try {
        setStatusCheck(true);
        const downloadRequestId = await createDownloadRequest(
          size,
          resourceData,
          collectionId
        );
        setDownloadId(downloadRequestId);
        if (resourceIds) {
          postAnalyticsEvent('image_library_download_resource', {
            resourceIds,
            imagesPerDownload: resourceIds.length,
          });
        } else if (collectionId) {
          postAnalyticsEvent('image_library_download_collection', {
            collectionId,
            imagesPerDownload: collectionSize ?? 0,
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    [collectionId, resourceData, resourceIds, size, collectionSize]
  );

  return (
    <div className="download-all">
      {showForm && !isLoggedIn && <UsageConsentForm onSuccess={onSubmit} />}
      {!showForm && !downloadId && !statusCheck && (
        <>
          <div>
            <select onChange={handleSizeChange}>
              <option value="">Original</option>
              <option value="ppp">Ideal for PowerPoint Presentations</option>
              <option value="mpr">Preview</option>
            </select>
          </div>
          <p>
            If your selected size is not available, the original file will be
            downloaded
          </p>
          <button onClick={handleDownloadClick}>Download</button>
        </>
      )}
      {statusCheck && <DownloadStatus downloadRequest={downloadRequest} />}
    </div>
  );
};
